import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { APP_ROUTES } from "../../../config/appRoutes";
import DescriptionIcon from '@mui/icons-material/Description';
import { getTemplateThunk } from "../templatesThunk";
import { useDispatch } from "react-redux";
import { resetTemplateState } from "../templatesSlice";
import { QR, QrActionButton } from "../../qrCode/components/qrUi";

// Header card for the template pages (same look as Campaigns / QR).
// Optional: `stats` (cards inside the header) and `children` (page content inside the card).
export default function PageButtons({ stats = [], children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [activePath, setActivePath] = useState(location.pathname);

    useEffect(() => {
        setActivePath(location.pathname);
    }, [location.pathname]);

    const handleClick = (path) => {
        setActivePath(path);
        navigate(path);
        dispatch(resetTemplateState());
    };

    useEffect(() => {
        dispatch(getTemplateThunk());
    }, [dispatch]);

    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                border: `1px solid ${QR.border}`,
                bgcolor: "#fff",
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
            }}
        >
            {/* Top row: title + one context button */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: { xs: 40, sm: 46 },
                            height: { xs: 40, sm: 46 },
                            borderRadius: 2.5,
                            color: "#fff",
                            background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
                            boxShadow: `0 8px 18px -8px ${QR.main}`,
                        }}
                    >
                        <DescriptionIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2} sx={{ fontSize: { xs: 17, sm: 20 } }}>
                            Templates
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                            Create WhatsApp message templates and track Meta approval
                        </Typography>
                    </Box>
                </Stack>

                {/* List page → "Create Template"; create page → "Back to List" */}
                <Box sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                    {activePath === APP_ROUTES.TEMPLATE.CREATION ? (
                        <QrActionButton
                            icon={<ArrowBackIcon sx={{ fontSize: 17 }} />}
                            label="Back to List"
                            variant="solid"
                            color="#fc8006" // logo orange
                            gradientTo="#fb923c"
                            onClick={() => handleClick(APP_ROUTES.TEMPLATE.LIST)}
                        />
                    ) : (
                        <QrActionButton
                            variant="solid"
                            color={QR.blue}
                            gradientTo="#3b82f6"
                            icon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                            label="Create Template"
                            onClick={() => handleClick(APP_ROUTES.TEMPLATE.CREATION)}
                        />
                    )}
                </Box>
            </Box>

            {/* Stats cards inside the header (only when the page passes stats) */}
            {stats.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${QR.border}`,
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${stats.length}, minmax(0, 1fr))` },
                        gap: 1.5,
                    }}
                >
                    {stats.map((s, idx) => (
                        <Stack
                            key={s.label}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                gridColumn: { xs: stats.length % 2 === 1 && idx === stats.length - 1 ? "span 2" : "auto", md: "auto" },
                                p: { xs: 1.25, sm: 1.5 },
                                borderRadius: 3,
                                bgcolor: "#fff",
                                border: `1px solid ${QR.border}`,
                                borderTop: `3px solid ${s.color}`,
                                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                            }}
                        >
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                                {s.icon}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                                    {s.label}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                                    {s.value}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Box>
            )}

            {/* Page content inside the same card */}
            {children && (
                <Box sx={{ mt: 2.5, pt: 0.5, borderTop: `1px solid ${QR.border}` }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}
